/**
 * Signaling WebSocket istemcisi — backend'e bağlanır + JSON mesajlarla
 * chat/system-action/privacy-curtain/adjust-encoder/wake-on-lan iletir.
 *
 * Reconnect: exponential backoff 1/2/4/8/16s, connection closed olduğunda
 * otomatik. Re-register state tutulur.
 */
export class SignalingClient {
    ws = null;
    url = '';
    jwt = '';
    registerMsg = null;
    reconnectAttempt = 0;
    shouldReconnect = true;
    onMessageCb = () => { };
    onStateCb = () => { };
    onReplacedCb = null;
    hbTimer = null;
    get connected() {
        return this.ws?.readyState === WebSocket.OPEN;
    }
    setOnMessage(cb) {
        this.onMessageCb = cb;
    }
    setOnState(cb) {
        this.onStateCb = cb;
    }
    /** Server "replaced_by_new_connection" gönderdiğinde tetiklenir. */
    setOnReplaced(cb) {
        this.onReplacedCb = cb;
    }
    connect(wsUrl, jwt) {
        this.url = wsUrl;
        this.jwt = jwt;
        this.shouldReconnect = true;
        return this.dial();
    }
    dial() {
        return new Promise((resolve, reject) => {
            const fullUrl = `${this.url}?token=${encodeURIComponent(this.jwt)}`;
            const ws = new WebSocket(fullUrl);
            ws.onopen = () => {
                this.reconnectAttempt = 0;
                this.onStateCb(true);
                if (this.registerMsg) {
                    ws.send(JSON.stringify(this.registerMsg));
                }
                this.startHeartbeat();
                resolve();
            };
            ws.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    this.onMessageCb(msg);
                }
                catch {
                    /* ignore invalid JSON */
                }
            };
            ws.onerror = () => {
                if (this.reconnectAttempt === 0)
                    reject(new Error('signaling error'));
            };
            ws.onclose = (ev) => {
                this.stopHeartbeat();
                this.onStateCb(false);
                // Server "başka bir client aynı device ID ile register oldu, sen
                // atıldın" anlamında close code 1008 (PolicyViolation) +
                // reason "replaced_by_new_connection" gönderir. Bu durumda kör
                // reconnect = ping-pong sonsuz döngüsü → kalıcı durdur.
                if (ev.code === 1008
                    || (ev.reason && ev.reason.includes('replaced_by_new_connection'))) {
                    this.shouldReconnect = false;
                    this.onReplacedCb?.();
                    return;
                }
                if (this.shouldReconnect) {
                    this.scheduleReconnect();
                }
            };
            this.ws = ws;
        });
    }
    scheduleReconnect() {
        const delays = [1000, 2000, 4000, 8000, 16000];
        const delay = delays[Math.min(this.reconnectAttempt, delays.length - 1)];
        this.reconnectAttempt++;
        setTimeout(() => {
            if (this.shouldReconnect) {
                void this.dial().catch(() => { });
            }
        }, delay);
    }
    register(deviceId, deviceJwt, capabilities) {
        this.registerMsg = {
            type: 'register',
            deviceId,
            deviceJwt,
            capabilities,
        };
        if (this.connected) {
            this.ws.send(JSON.stringify(this.registerMsg));
        }
    }
    send(msg) {
        if (!this.connected)
            return false;
        this.ws.send(JSON.stringify(msg));
        return true;
    }
    // ---- High-level helpers ----
    sendChat(sessionId, sender, text) {
        this.send({
            type: 'chat',
            sessionId, sender, text,
            sentAt: new Date().toISOString(),
        });
    }
    sendSystemAction(sessionId, action) {
        this.send({ type: 'system-action', sessionId, action });
    }
    sendPrivacyCurtain(sessionId, active) {
        this.send({ type: 'set-privacy-curtain', sessionId, active });
    }
    sendRequestKeyframe(sessionId) {
        this.send({ type: 'request-keyframe', sessionId });
    }
    sendAdjustEncoder(sessionId, targetBitrateBps) {
        this.send({ type: 'adjust-encoder', sessionId, targetBitrateBps });
    }
    sendWakeOnLan(targetDeviceId, macAddress) {
        this.send({ type: 'wake-on-lan', targetDeviceId, macAddress });
    }
    /**
     * Faz V: Controller → Host Offer. Web SPA raw UDP yapamadığı için candidates
     * boş + controllerTransport="wss" — signaling server Offer'a ForceRelay +
     * RelaySessionId inject eder, host-agent P2P'yi atlar + relay'e bind eder.
     */
    sendOffer(args) {
        return this.send({
            type: 'offer',
            sessionId: args.sessionId,
            fromDeviceId: args.fromDeviceId,
            toDeviceId: args.toDeviceId,
            candidates: [],
            noiseHandshakeStage1Base64: '',
            monitorIndex: args.monitorIndex ?? null,
            controllerTransport: 'wss',
        });
    }
    close() {
        this.shouldReconnect = false;
        this.stopHeartbeat();
        this.ws?.close();
        this.ws = null;
    }
    startHeartbeat() {
        this.stopHeartbeat();
        this.hbTimer = window.setInterval(() => {
            if (this.connected) {
                this.send({ type: 'heartbeat', clientTicks: Date.now() });
            }
        }, 15000);
    }
    stopHeartbeat() {
        if (this.hbTimer !== null) {
            clearInterval(this.hbTimer);
            this.hbTimer = null;
        }
    }
}
